import * as pdfjsLib from 'pdfjs-dist';
import { PDF_EXTRACTION_PAGE_COUNT, OCR_EXTRACTION_ENABLED } from './RRDPdfConfig';
import { readFileAsArrayBuffer } from './RRDPdfCore';
import { ocrPage } from './RRDPdfOcrService';

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

export type PageText = {
  fullText: string;
  fullTextUpper: string;
  lines: string[];
};

export type PdfTextExtractionOptions = {
  onDocumentLoaded?: (pageCount: number) => void;
  shouldContinue?: (pageTexts: PageText[]) => boolean;
};

// A page's embedded text is only trusted once it has at least this many
// alphanumeric characters — a few stray bytes from a corrupted flate stream
// don't count as "extractable text" and should still fall back to OCR.
const MIN_USABLE_TEXT_LENGTH = 100;
// Two render scales are tried for OCR: the higher scale first for accuracy,
// falling back to a lower scale if rendering/recognition failed.
const OCR_SCALES = [1.5, 2.0];

function linesFromTextItems(textItems: any[]): string[] {
  const lines: string[] = [];
  let currentLine = '';
  for (const item of textItems) {
    const token = (item?.str || '').trim();
    if (!token) {
      if (item?.hasEOL && currentLine.trim()) {
        lines.push(currentLine.replace(/\s+/g, ' ').trim());
        currentLine = '';
      }
      continue;
    }

    currentLine = currentLine ? `${currentLine} ${token}` : token;
    if (item?.hasEOL) {
      lines.push(currentLine.replace(/\s+/g, ' ').trim());
      currentLine = '';
    }
  }
  if (currentLine.trim()) {
    lines.push(currentLine.replace(/\s+/g, ' ').trim());
  }
  return lines;
}

/**
 * Reads a single PDF page's text content (with an OCR fallback for
 * scanned/corrupt pages) and returns normalized text plus rough lines.
 */
async function extractPageText(page: any): Promise<PageText> {
  const textContent = await page.getTextContent();
  const textItems = textContent.items as any[];
  let fullText = textItems.map((item: any) => item.str).join(' ').replace(/\s+/g, ' ').trim();
  let lines = linesFromTextItems(textItems);

  const hasUsableText = fullText.replace(/[^a-z0-9]/gi, '').length >= MIN_USABLE_TEXT_LENGTH;
  if (!hasUsableText && OCR_EXTRACTION_ENABLED) {
    for (const scale of OCR_SCALES) {
      try {
        const ocrText = await ocrPage(page, scale);
        if (ocrText) {
          fullText = ocrText.replace(/\s+/g, ' ').trim();
          lines = ocrText.split('\n').map((ln) => ln.replace(/\s+/g, ' ').trim()).filter(Boolean);
          break;
        }
        console.warn('[PDF OCR] tesseract returned no text at scale', scale);
      } catch (ocrErr) {
        console.error('[PDF OCR] OCR attempt failed at scale', scale, ocrErr);
      }
    }
  }

  return { fullText, fullTextUpper: fullText.toUpperCase(), lines };
}

/**
 * Loads a PDF once and extracts text (with OCR fallback) for up to
 * `maxPages` pages. This is the single shared parsing pass used by both
 * document classification and field extraction, so a PDF is never opened,
 * rendered, or OCR'd more than once per upload.
 */
export async function extractPdfPageTexts(
  file: File,
  maxPages: number = PDF_EXTRACTION_PAGE_COUNT,
  options: PdfTextExtractionOptions = {},
): Promise<PageText[]> {
  const buffer = await readFileAsArrayBuffer(file);
  const loadingTask = pdfjsLib.getDocument({ data: buffer.slice(0) });
  const pdf = await loadingTask.promise;
  options.onDocumentLoaded?.(pdf.numPages);

  const pageCountToProcess = Math.min(pdf.numPages, Math.max(1, maxPages));
  const pageTexts: PageText[] = [];

  try {
    for (let pageNum = 1; pageNum <= pageCountToProcess; pageNum++) {
      const page = await pdf.getPage(pageNum);
      try {
        pageTexts.push(await extractPageText(page));
      } finally {
        page.cleanup();
      }
      if (pageNum < pageCountToProcess && options.shouldContinue && !options.shouldContinue(pageTexts)) {
        break;
      }
    }
  } finally {
    await pdf.destroy();
  }

  return pageTexts;
}
